import {
  Box,
  Card,
  Text,
  TileIconSquare,
  type Color,
  type IconName,
} from "@salestrak/ui";
import type { WidgetSeed } from "#app/widgets/dashboards/use-widget-view";
import {
  DASHBOARD_TEMPLATES,
  type DashboardTemplate,
} from "#app/widgets/dashboards/settings/dashboard-templates";
import type { Omittable } from "@salestrak/ui";

export type DashboardTemplateChooserProps = {
  onSelectTemplate: (seeds: WidgetSeed[], template: DashboardTemplate) => void;
  onStartFromScratch: () => void;
};

/** A subtle hover border cue on top of the stock card. Radius comes from the
 *  Card `radius` prop so the inner highlight ring stays in lockstep — a
 *  `rounded-*` class here would leave the ring at the default radius.
 *  DESIGN-TUNABLE. */
const TEMPLATE_TILE_CLASS =
  "cursor-pointer text-left transition-colors hover:border-(--border-strong)";

/** Template tiles: one column on phones, two from `sm` up. The library ships
 *  no grid primitive, so the layout is a class. */
const TEMPLATE_GRID = "grid grid-cols-1 sm:grid-cols-2";

function TemplateTile({
  icon,
  color,
  label,
  description,
  onClick,
}: Omittable<{
  icon: IconName;
  color?: Color;
  label: string;
  description: string;
  onClick: () => void;
}>) {
  return (
    <Card
      render={<Box as="button" />}
      className={TEMPLATE_TILE_CLASS}
      radius="lg"
      onClick={onClick}
      aria-label={label}
    >
      <Box direction="column" gap="sm" paddingX="md" paddingY="md">
        <Box direction="row" align="center">
          <Box pointerEvents="none">
            <TileIconSquare icon={icon} color={color} />
          </Box>
        </Box>
        <Box direction="column" gap="2xs">
          <Text size="sm" weight="semibold" truncate>
            {label}
          </Text>
          <Text size="xs" variant="muted" truncate={2}>
            {description}
          </Text>
        </Box>
      </Box>
    </Card>
  );
}

/**
 * The template tile grid — every template plus one escape tile ("Start from
 * scratch"), so declining a template is a tile like any other.
 */
function DashboardTemplateGrid({
  onSelectTemplate,
  escapeTile,
}: {
  onSelectTemplate: (template: DashboardTemplate) => void;
  escapeTile: { icon: IconName; label: string; description: string; onClick: () => void };
}) {
  return (
    <Box gap="md" className={TEMPLATE_GRID}>
      {DASHBOARD_TEMPLATES.map((template) => (
        <TemplateTile
          key={template.key}
          icon={template.icon}
          color={template.color}
          label={template.label}
          description={template.description}
          onClick={() => onSelectTemplate(template)}
        />
      ))}
      <TemplateTile
        icon={escapeTile.icon}
        label={escapeTile.label}
        description={escapeTile.description}
        onClick={escapeTile.onClick}
      />
    </Box>
  );
}

export function DashboardTemplateChooser({
  onSelectTemplate,
  onStartFromScratch,
}: DashboardTemplateChooserProps) {
  return (
    <Box direction="column" align="center" paddingY="2xl">
      <Box direction="column" width="100%" maxWidth={600} gap="xl">
        <Box direction="column" align="center" gap="2xs">
          <Text size="xl" weight="semibold">
            Choose a dashboard template
          </Text>
          <Text size="sm" variant="muted">
            Get started with a template, or build your own from scratch.
          </Text>
        </Box>
        <DashboardTemplateGrid
          onSelectTemplate={(template) => onSelectTemplate(template.seeds, template)}
          escapeTile={{
            icon: "plus",
            label: "Start from scratch",
            description: "Open the widget gallery and choose each widget yourself.",
            onClick: onStartFromScratch,
          }}
        />
      </Box>
    </Box>
  );
}
